import React, { useState, useEffect } from 'react';
import Footer from '../components/Footer';
import BBBSeal from '../components/BBBSeal';

interface Review {
  id: string;
  title: string;
  author: string;
  product: string;
  rating: number;
  text: string;
  createdAt: string;
}

const ReviewsPage: React.FC = () => {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
  const [sortBy, setSortBy] = useState('newest');
  const [reviews, setReviews] = useState<Review[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAddReview, setShowAddReview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  
  const [newReview, setNewReview] = useState({
    title: '',
    author: '',
    product: 'ez-care',
    rating: 5,
    text: '',
  });

  // Fetch reviews from API
  useEffect(() => {
    const fetchReviews = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          sortBy: sortBy,
          limit: '50',
        });
        
        const response = await fetch(`${API_URL}/api/reviews?${params}`);
        const data = await response.json();
        
        setReviews(data.reviews || []);
        setTotalCount(data.totalCount || 0);
      } catch (error) {
        console.error('Error fetching reviews:', error);
        // Fallback to sample data if API fails
        setReviews([
          {
            id: '1',
            title: "My fall detection worked perfectly!",
            author: "Linda",
            product: "On-the-Go + Home Fall Detection",
            rating: 5,
            text: "I am very happy with Alert1. My fall detection worked for me and I am telling my friends about you. Great job everyone!",
            createdAt: "2025-08-05T00:00:00.000Z"
          },
          {
            id: '2',
            title: "So easy to work with",
            author: "Sharon R.",
            product: "Home EZ Care Plus",
            rating: 5,
            text: "Alert1 is such a great company! They are very easy to work with. I am a teacher and am always raving about Alert1 to my colleagues.",
            createdAt: "2025-05-21T00:00:00.000Z"
          }
        ]);
        setTotalCount(2); // Fallback count if API fails
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, [sortBy, API_URL]);

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    setSubmitSuccess(false);

    try {
      const response = await fetch(`${API_URL}/api/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newReview),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit review');
      }

      setSubmitSuccess(true);
      setNewReview({
        title: '',
        author: '',
        product: 'ez-care',
        rating: 5,
        text: '',
      });
      
      // Refresh reviews after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error: any) {
      setSubmitError(error.message || 'Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="reviews-page">
      {/* Hero Section */}
      <section className="reviews-hero-section">
        <div className="container">
          <h1 className="reviews-hero-title">Alert1 Reviews</h1>
          <div className="reviews-hero-trust">
            <p className="reviews-hero-trust-text">Rated 4.6 / 5 on Google Customer Reviews</p>
            <BBBSeal variant="rating" />
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="reviews-main-section">
        <div className="reviews-container">
          <div className="reviews-sidebar">
            <div className="reviews-count-box">
              <h2 className="reviews-count">{totalCount.toLocaleString()}</h2>
              <p className="reviews-count-label">TOTAL REVIEWS</p>
            </div>
            <button 
              className="reviews-add-btn" 
              onClick={() => setShowAddReview(true)}
            >
              ADD REVIEW
            </button>
          </div>

          <div className="reviews-content">
            <div className="reviews-filters">
              <div className="reviews-filter-group">
                <label className="reviews-filter-label">Sort By</label>
                <select 
                  className="reviews-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  <option value="highest">Highest Rated</option>
                  <option value="lowest">Lowest Rated</option>
                </select>
              </div>
            </div>

            <div className="reviews-list">
              {loading ? (
                <div style={{ textAlign: 'center', padding: '3rem', fontSize: '1.2rem', color: '#666' }}>
                  Loading reviews...
                </div>
              ) : reviews.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', fontSize: '1.2rem', color: '#666' }}>
                  No reviews found. Be the first to leave a review!
                </div>
              ) : (
                reviews.map((review) => (
                  <div key={review.id} id={`review-${review.id}`} className="review-card">
                    <div className="review-header">
                      <div className="review-title-section">
                        <h3 className="review-title">{review.title}</h3>
                        <p className="review-author">{review.author}</p>
                        <p className="review-product">
                          Product: <em>{review.product}</em>
                        </p>
                      </div>
                      <div className="review-stars">
                        {[...Array(5)].map((_, index) => (
                          <svg key={index} width="28" height="28" viewBox="0 0 24 24" fill={index < review.rating ? "#f4a442" : "#e0e0e0"}>
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                          </svg>
                        ))}
                      </div>
                    </div>
                    <p className="review-text">{review.text}</p>
                    <p className="review-date">Posted on {new Date(review.createdAt).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Right Sidebar */}
        <aside className="reviews-sidebar-right">
          {/* Our Commitment Section */}
          <div className="reviews-commitment">
            <h3 className="reviews-commitment-title">Our Commitment</h3>
            <p className="reviews-commitment-text">
              Alert1 Medical Alert Systems provides emergency monitoring service across the entire United States, Canada, and Puerto Rico. Our professional agents work with your local emergency services—EMT, Fire, Police, etc.—to ensure you or your loved one gets help immediately in any emergency. Best of all, we stay on the line until help arrives. Our members never face an emergency alone. Our monitoring centers are US-based, which means we do not outsource any of our services to another country. Every one of our Senior Care team members and Command Center agents are located in the United States. These reviews were submitted by our very own Alert1 members. Join today and get access to fast help at the push of a button, and <strong>never face an emergency alone</strong> again!
            </p>
          </div>

          {/* Our Policy Section */}
          <div className="reviews-policy">
            <h3 className="reviews-policy-title">Our Policy</h3>
            <div className="reviews-policy-list">
              <div className="reviews-policy-item">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="reviews-policy-icon">
                  <rect x="1" y="3" width="15" height="13"></rect>
                  <path d="M16 6h5l3 7v5h-2.5"></path>
                  <circle cx="5.5" cy="18.5" r="2.5"></circle>
                  <circle cx="18.5" cy="18.5" r="2.5"></circle>
                </svg>
                <span>FREE FedEx priority shipping!</span>
              </div>
              <div className="reviews-policy-item">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="reviews-policy-icon">
                  <path d="M3 3h18v18H3z"></path>
                  <path d="M12 8v8"></path>
                  <path d="M8 12h8"></path>
                </svg>
                <span>Systems are pre-programmed-- just grab and go!</span>
              </div>
              <div className="reviews-policy-item">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="reviews-policy-icon">
                  <path d="M23 12a11.05 11.05 0 0 0-22 0zm-5 7a3 3 0 0 1-6 0v-7"></path>
                </svg>
                <span>No long-term contract required; cancel any time by returning the equipment.</span>
              </div>
            </div>
          </div>

          {/* Need Help Section */}
          <div className="reviews-help">
            <h3 className="reviews-help-title">Need Help?</h3>
            <p className="reviews-help-text">Call us. We'll be glad to answer your questions!</p>
            <a href="tel:1-877-573-4195" className="reviews-help-phone">1-877-573-4195</a>
          </div>
        </aside>
      </section>

      {/* Add Review Modal */}
      {showAddReview && (
        <div 
          className="reviews-modal-overlay" 
          onClick={() => !submitting && setShowAddReview(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div 
            className="reviews-modal" 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '2rem',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto',
            }}
          >
            <h2 style={{ marginTop: 0, marginBottom: '1.5rem' }}>Add Your Review</h2>
            
            {submitSuccess && (
              <div style={{
                padding: '1rem',
                backgroundColor: '#d4edda',
                color: '#155724',
                borderRadius: '4px',
                marginBottom: '1rem',
              }}>
                Thank you for your review! It has been submitted and will be reviewed before being published.
              </div>
            )}

            {submitError && (
              <div style={{
                padding: '1rem',
                backgroundColor: '#f8d7da',
                color: '#721c24',
                borderRadius: '4px',
                marginBottom: '1rem',
              }}>
                {submitError}
              </div>
            )}

            <form onSubmit={handleSubmitReview}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Your Name *
                </label>
                <input
                  type="text"
                  required
                  value={newReview.author}
                  onChange={(e) => setNewReview({ ...newReview, author: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem',
                  }}
                  placeholder="Enter your name"
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Review Title *
                </label>
                <input
                  type="text"
                  required
                  value={newReview.title}
                  onChange={(e) => setNewReview({ ...newReview, title: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem',
                  }}
                  placeholder="Give your review a title"
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Product *
                </label>
                <select
                  required
                  value={newReview.product}
                  onChange={(e) => setNewReview({ ...newReview, product: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem',
                  }}
                >
                  <option value="ez-care">EZ Care In-Home</option>
                  <option value="ez-care-plus">EZ Care Plus In-Home</option>
                  <option value="lifebeacon">LifeBeacon On-the-Go</option>
                </select>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Rating *
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => setNewReview({ ...newReview, rating })}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                      }}
                    >
                      <svg 
                        width="32" 
                        height="32" 
                        viewBox="0 0 24 24" 
                        fill={rating <= newReview.rating ? "#f4a442" : "#e0e0e0"}
                      >
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                    </button>
                  ))}
                  <span style={{ marginLeft: '0.5rem', color: '#666' }}>
                    {newReview.rating} of 5 stars
                  </span>
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Your Review *
                </label>
                <textarea
                  required
                  value={newReview.text}
                  onChange={(e) => setNewReview({ ...newReview, text: e.target.value })}
                  rows={6}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    fontFamily: 'inherit',
                  }}
                  placeholder="Tell us about your experience..."
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowAddReview(false)}
                  disabled={submitting}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    background: 'white',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    borderRadius: '4px',
                    background: submitting ? '#ccc' : '#007bff',
                    color: 'white',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                  }}
                >
                  {submitting ? 'Submitting...' : 'Submit Review'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ReviewsPage;

